/**
 * Permission keys (CLAUDE.md rule 6: every command checks one on the server).
 *
 * The v3.0 catalogue, derived from every module's commands and screens in
 * 03-modules.md, 05-api.md and 08-clients.md. Keys are dotted snake_case,
 * `group.action` or `group.action.qualifier` (`orders.discount.above_limit`,
 * `reports.view.sales`). Once a key ships it is stable: roles store it in
 * `iam.role_permission.permission_key`, so rename only by adding a new key and
 * migrating role rows.
 *
 * Every key has:
 * - `label` and `group`: what the role editor shows.
 * - `scope`:
 *     `pos`        usable in till sessions (PIN or password on a till terminal)
 *                  and in back-office sessions;
 *     `backoffice` only in back-office sessions (browser or a `backoffice`
 *                  terminal, with 2FA for roles that require it);
 *     `self`       implicit: every signed-in staff user holds it for their own
 *                  account; never stored on a role;
 *     `platform`   QRkey staff only (user_account.is_platform_staff); never
 *                  assignable to a tenant role (the database refuses it too).
 *     `system`     held only by non-human contexts (worker jobs, webhook
 *                  processing; systemCtx/connectorCtx in apps/api), never by a
 *                  person or a role (the database refuses `system.%` on roles).
 *     `customer`   held only by customer actors (a diner's table session in the
 *                  customer app, storefront); never by staff or a role (the
 *                  database refuses `store.%` on roles).
 * - `overridable`: lacking it on a till answers `403 OVERRIDE_REQUIRED`, and a
 *   manager's PIN can approve the single action (`POST /v1/auth/override`).
 *   Otherwise the answer is a plain `403 FORBIDDEN`.
 *
 * TODO(legacy): reconcile with the 105 legacy permissions (portal
 * `role-management`, `AdminController`, the three JWT middlewares) once the
 * legacy code is available; fill LEGACY_MAPPING and add any missing key.
 */
import { z } from 'zod';

export const PERMISSION_SCOPES = [
  'pos',
  'backoffice',
  'self',
  'platform',
  'system',
  'customer',
] as const;
export type PermissionScope = (typeof PERMISSION_SCOPES)[number];

/** Role-editor groups, in display order. */
export const PERMISSION_GROUPS = {
  outlet: 'Outlet setup',
  staff: 'Staff and roles',
  menu: 'Menu',
  orders: 'Orders and POS',
  kitchen: 'KOT, KDS and printing',
  pricing: 'Tax, discounts and charges',
  payments: 'Payments',
  accounting: 'Day close, shifts and cash',
  reports: 'Reports',
  inventory: 'Inventory and recipes',
  bar: 'Liquor and bar',
  customers: 'Customers, khata and rewards',
  notifications: 'Messages and alerts',
  aggregators: 'Aggregators',
  delivery: 'Delivery partners',
  integrations: 'Integrations',
  self: 'Own account',
  platform: 'QRkey platform',
  system: 'Background jobs and webhooks',
  store: 'Customer app (QR and online ordering)',
} as const;

export type PermissionGroup = keyof typeof PERMISSION_GROUPS;

export interface PermissionDefinition {
  readonly label: string;
  readonly group: PermissionGroup;
  readonly scope: PermissionScope;
  readonly overridable?: boolean;
}

const pos = (group: PermissionGroup, label: string, overridable = false): PermissionDefinition => ({
  label,
  group,
  scope: 'pos',
  ...(overridable ? { overridable: true } : {}),
});
const office = (group: PermissionGroup, label: string): PermissionDefinition => ({
  label,
  group,
  scope: 'backoffice',
});

/** The catalogue. Order is the display order within each group. */
export const PERMISSION_CATALOGUE = {
  // Outlet setup (org)
  'outlet.view': pos('outlet', 'View outlet profile and settings'),
  'outlet.create': office('outlet', 'Create outlets'),
  'outlet.settings': office('outlet', 'Edit outlet profile and settings'),
  'outlet.tables.manage': office('outlet', 'Manage sections, tables and table QR codes'),
  'outlet.timing_groups.manage': office('outlet', 'Manage timing groups'),
  'outlet.charges.manage': office('outlet', 'Manage other charges'),
  'outlet.payment_methods.manage': office('outlet', 'Manage payment methods'),
  'outlet.order_sources.manage': office('outlet', 'Manage order sources'),
  'outlet.feature_switches': office('outlet', 'Turn outlet features on or off'),
  'terminals.view': office('outlet', 'View registered terminals'),
  'terminals.register': office('outlet', 'Register a terminal (till, captain, KDS, hub)'),
  'terminals.block': office('outlet', 'Block a terminal'),

  // Staff and roles (iam)
  'staff.view': office('staff', 'View staff'),
  'staff.manage': office('staff', 'Invite, edit and deactivate staff'),
  'staff.pin.set': office('staff', "Set another staff member's PIN"),
  'staff.offline_pins': pos('staff', 'Cache staff PINs on this till for offline sign-in'),
  'roles.view': office('staff', 'View roles and their permissions'),
  'roles.manage': office('staff', 'Create and edit roles, grant company-wide access'),
  'audit.view': office('staff', 'View the audit log'),
  'overrides.approve': pos('staff', 'Approve an override with a PIN'),

  // Menu
  'menu.view': pos('menu', 'View the menu'),
  'menu.edit': office('menu', 'Edit categories, products, variants and add-ons'),
  'menu.prices': office('menu', 'Change prices'),
  'menu.availability': pos('menu', 'Mark items sold out or available'),
  'menu.publish': office('menu', 'Validate and publish the menu'),
  'menu.import_export': office('menu', 'Import and export the menu (Excel)'),

  // Orders and POS
  'orders.view': pos('orders', 'View orders and the table map'),
  'orders.create': pos('orders', 'Open orders'),
  'orders.edit': pos('orders', 'Add and update lines'),
  'orders.send_kot': pos('orders', 'Send to kitchen (KOT)'),
  'orders.void': pos('orders', 'Void a line not yet sent to the kitchen', true),
  'orders.void_sent': pos('orders', 'Void a line already sent to the kitchen', true),
  'orders.discount': pos('orders', 'Apply a discount within the role limit', true),
  'orders.discount.above_limit': pos('orders', 'Apply a discount above the role limit', true),
  'orders.charges': pos('orders', 'Change or waive charges on an order', true),
  'orders.complimentary': pos('orders', 'Mark lines or orders complimentary', true),
  'orders.bill': pos('orders', 'Print or show the bill'),
  'orders.reopen': pos('orders', 'Reopen a billed order', true),
  'orders.split': pos('orders', 'Split an order'),
  'orders.merge': pos('orders', 'Merge orders'),
  'orders.move_table': pos('orders', 'Move an order to another table'),
  'orders.transfer_lines': pos('orders', 'Transfer lines between orders'),
  'orders.cancel': pos('orders', 'Cancel an order', true),
  'orders.accept': pos('orders', 'Accept QR, online and aggregator orders'),
  'orders.reject': pos('orders', 'Reject QR, online and aggregator orders', true),
  'orders.fulfilment': pos('orders', 'Update fulfilment status (ready, served, picked up)'),
  'orders.tag': pos('orders', 'Tag orders'),
  'orders.bulk': office('orders', 'Settle or cancel many orders at once'),
  'orders.sync_offline': pos('orders', 'Sync orders taken offline'),

  // KOT, KDS and printing (kitchen)
  'kitchen.setup': office('kitchen', 'Manage stations, printers, routing and templates'),
  'kitchen.kds': pos('kitchen', 'Bump and recall KDS tickets'),
  'kitchen.reprint_kot': pos('kitchen', 'Reprint a KOT (marked DUPLICATE)', true),
  'kitchen.print_bill': pos('kitchen', 'Print a bill'),
  'kitchen.reprint_bill': pos('kitchen', 'Reprint a bill', true),
  'kitchen.test_print': pos('kitchen', 'Print a test page'),
  'kitchen.print_device': pos(
    'kitchen',
    "Print jobs on this terminal's printers and report the result",
  ),

  // Tax, discounts and charges (pricing)
  'pricing.taxes.manage': office('pricing', 'Manage tax groups and tax rules'),
  'pricing.discounts.manage': office('pricing', 'Manage discounts'),
  'pricing.discount_limits': office('pricing', 'Set discount limits per role'),
  // TODO(M2 decision): unused. Charges are org's other_charge, managed with
  // outlet.charges.manage; 04-data-model.md has no charge_rule table. Drop this key
  // or move charge management to it (apps/api/src/modules/pricing/README.md, issue 6).
  'pricing.charges.manage': office('pricing', 'Manage charge rules'),

  // Payments
  'payments.view': pos('payments', 'View payments and pending settlements'),
  'payments.record': pos('payments', 'Record tenders (cash, card, UPI)'),
  'payments.online': pos('payments', 'Create online payment requests (UPI / Razorpay)'),
  'payments.void': pos('payments', 'Void a tender', true),
  'payments.mode_correct': pos('payments', 'Correct the payment mode after billing', true),
  'payments.settle': pos('payments', 'Settle orders'),
  'payments.pending_settlement': pos('payments', 'Mark orders pending settlement'),

  // Day close, shifts and cash (accounting)
  'day.open': pos('accounting', 'Open the business day'),
  'day.close': pos('accounting', 'Close the business day', true),
  'day.close_force': pos(
    'accounting',
    'Close the business day with unsettled orders or unconfirmed KOTs still open',
    true,
  ),
  'shift.open': pos('accounting', 'Open a shift'),
  'shift.close': pos('accounting', 'Close a shift with a cash count'),
  'cash.in_out': pos('accounting', 'Record cash in and cash out'),
  'cash.transfer': office('accounting', 'Transfer cash between cash accounts'),
  'expenses.record': pos('accounting', 'Record expenses'),
  'expenses.manage': office('accounting', 'Manage expense categories and cash accounts'),
  'expenses.approve': office('accounting', 'Approve or reject expenses above the approval limit'),
  'accounting.view': office('accounting', 'View cash position and day summaries'),
  'invoices.view': pos('accounting', 'View GST invoices'),
  'invoices.series': office('accounting', 'Set up GST invoice series (prefix, numbering)'),

  // Reports
  'reports.dashboard': office('reports', 'View the outlet dashboard'),
  'reports.view.sales': office('reports', 'View the sales report'),
  'reports.view.items': office('reports', 'View the items report'),
  'reports.view.tax': office('reports', 'View the tax report'),
  'reports.view.payments': office('reports', 'View the payments and settlement report'),
  'reports.view.voids_discounts': office('reports', 'View the voids and discounts report'),
  'reports.export': office('reports', 'Export reports (CSV, Excel, PDF)'),
  'reports.rebuild': office('reports', 'Rebuild a day’s report figures from the orders'),

  // Inventory and recipes
  'inventory.view': office('inventory', 'View stock, ledger and consumption'),
  'inventory.items.view': pos('inventory', 'View the stock item list (to record wastage)'),
  'inventory.items.manage': office('inventory', 'Manage stock items, units and categories'),
  'inventory.vendors.manage': office('inventory', 'Manage vendors and storage sections'),
  'inventory.recipes.manage': office('inventory', 'Manage recipes'),
  'inventory.stock_in': office('inventory', 'Record stock in'),
  'inventory.moves': office('inventory', 'Move stock between storage sections'),
  'inventory.wastage.record': pos('inventory', 'Record wastage'),
  'inventory.wastage.approve': office('inventory', 'Approve wastage above the threshold'),
  'inventory.counts.submit': office('inventory', 'Run and submit stock counts'),
  'inventory.counts.approve': office('inventory', 'Approve stock count variances'),

  // Liquor and bar
  'bar.view': pos('bar', 'View liquor profiles, permits and closing counts'),
  'bar.profiles.manage': office('bar', 'Manage liquor profiles'),
  'bar.permits.manage': office('bar', 'Import and manage permit numbers'),
  'bar.permits.assign': pos('bar', 'Assign a permit to an order'),
  'bar.breakage': pos('bar', 'Record breakage'),
  'bar.closing': pos('bar', 'Record bar closing counts'),
  'bar.reports': office('bar', 'View excise and bar consumption reports'),

  // Messages and alerts (notifications, M5 task 7)
  'notifications.view': office(
    'notifications',
    'View message templates, settings and the delivery log',
  ),
  'notifications.manage': office(
    'notifications',
    'Edit message templates, channels and alert settings',
  ),
  'notifications.test_send': office('notifications', 'Send a test message'),
  'notifications.retry': office('notifications', 'Retry a message that failed'),
  'notifications.optouts': pos('notifications', 'Record or lift a customer’s message opt-out'),

  // Customers, khata and rewards
  'customers.view': pos('customers', 'Find and view customers'),
  'customers.manage': pos('customers', 'Add and edit customers and addresses'),
  'customers.import': office('customers', 'Import customers'),
  'customers.privacy': office('customers', 'Export or erase customer data (DPDP)'),
  'khata.view': pos('customers', 'View khata accounts, balances and statements'),
  'khata.open': pos('customers', 'Open a khata (credit) account'),
  'khata.limit': office('customers', 'Change a khata credit limit or suspend an account'),
  'khata.charge': pos('customers', 'Charge an order to khata within the limit'),
  'khata.charge.above_limit': pos('customers', 'Charge khata above the credit limit', true),
  'khata.settle': pos('customers', 'Record khata payments'),
  'rewards.redeem': pos('customers', 'Redeem rewards at billing'),
  'rewards.adjust': office('customers', 'Adjust reward balances'),
  'rewards.programs.manage': office('customers', 'Manage reward programs and slabs'),

  // Aggregators
  'aggregators.view': pos('aggregators', 'View aggregator orders and store status'),
  'aggregators.store_status': pos('aggregators', 'Take aggregator stores online or offline'),
  'aggregators.support_action': pos('aggregators', 'Raise partner-support actions'),
  'aggregators.manage': office('aggregators', 'Manage aggregator stores, item map, auto-accept'),
  'aggregators.menu_push': office('aggregators', 'Push the menu to aggregators'),
  'aggregators.settlements': office('aggregators', 'View and import aggregator settlements'),
  'aggregators.retry': office('aggregators', 'Retry failed menu pushes and availability syncs'),

  // Delivery partners
  'delivery.view': pos('delivery', 'View deliveries'),
  'delivery.dispatch': pos('delivery', 'Get quotes, book, cancel and verify deliveries'),
  'delivery.zones.manage': office('delivery', 'Manage delivery zones and slots'),
  'delivery.settings': office(
    'delivery',
    'Choose how delivery partners are picked and the delivery markup',
  ),
  'delivery.return': pos('delivery', 'Record that a failed delivery came back'),

  // Integrations
  'integrations.view': office('integrations', 'View connectors and their health'),
  'integrations.manage': office('integrations', 'Install and configure connectors'),

  // Own account: implicit for every signed-in staff user, never on a role.
  'self.session': { label: 'Use own sessions', group: 'self', scope: 'self' },
  'self.security': {
    label: 'Manage own password and two-factor authentication',
    group: 'self',
    scope: 'self',
  },
  'self.override_request': {
    label: 'Ask a manager to approve an action',
    group: 'self',
    scope: 'self',
  },

  // QRkey platform: QRkey staff only, never on a tenant role.
  'platform.dead_letter_view': {
    label: 'View dead-lettered jobs',
    group: 'platform',
    scope: 'platform',
  },
  'platform.dead_letter_retry': {
    label: 'Retry dead-lettered jobs',
    group: 'platform',
    scope: 'platform',
  },
  'platform.company_create': {
    label: 'Create a company (onboard a restaurant)',
    group: 'platform',
    scope: 'platform',
  },
  'platform.outlet_routing': {
    label: 'Switch an outlet between legacy and v3',
    group: 'platform',
    scope: 'platform',
  },

  // System: jobs and webhooks only, never a person (M1 task 8).
  'system.integrations': {
    label: 'Record connector webhooks, health and call results',
    group: 'system',
    scope: 'system',
  },
  'system.kitchen': {
    label: 'Create KOTs from orders and retry or fail print jobs',
    group: 'system',
    scope: 'system',
  },
  'system.payments': {
    label: 'Confirm online payments from payment gateway webhooks',
    group: 'system',
    scope: 'system',
  },
  'system.storefront': {
    label: 'Send accepted QR orders to the kitchen and end table visits',
    group: 'system',
    scope: 'system',
  },
  'system.inventory': {
    label: 'Deduct stock for orders from order and kitchen events',
    group: 'system',
    scope: 'system',
  },
  'system.bar': {
    label: 'Assign permit numbers to liquor orders from order events',
    group: 'system',
    scope: 'system',
  },
  'system.customers': {
    label: 'Earn and expire customer rewards from order events and the nightly job',
    group: 'system',
    scope: 'system',
  },
  'system.notifications': {
    label: 'Queue, send and report messages, popups and ops alerts from events and schedules',
    group: 'system',
    scope: 'system',
  },
  'system.aggregators': {
    label:
      'Place aggregator orders, accept them by rule, keep stores online and report partner results from webhooks and jobs',
    group: 'system',
    scope: 'system',
  },
  'system.delivery': {
    label:
      'Book and cancel courier deliveries, record rider updates from webhooks and track costs from events and jobs',
    group: 'system',
    scope: 'system',
  },
  'system.reports': {
    label: 'Keep the daily report aggregates up to date from order, payment and day events',
    group: 'system',
    scope: 'system',
  },

  // Customer app (storefront, M3 task 6): customer actors only, never a person on staff.
  'store.order': {
    label: 'Order at the table, add rounds and ask for the bill',
    group: 'store',
    scope: 'customer',
  },
  'store.pay': { label: 'Pay an own order online', group: 'store', scope: 'customer' },
  'store.rate': { label: 'Rate an own order', group: 'store', scope: 'customer' },
  // Takeaway and delivery from the customer app (M5 task 6): a signed-in customer.
  'store.account': {
    label: 'Keep an own profile, addresses and saved carts, and see own order history',
    group: 'store',
    scope: 'customer',
  },
  'store.online_order': {
    label: 'Place takeaway and delivery orders, pay and follow them',
    group: 'store',
    scope: 'customer',
  },
} as const satisfies Record<string, PermissionDefinition>;

export type PermissionKey = keyof typeof PERMISSION_CATALOGUE;

export const PERMISSION_KEYS = Object.freeze(
  Object.keys(PERMISSION_CATALOGUE) as PermissionKey[],
) as readonly PermissionKey[];

/**
 * Shape every permission key follows, including keys added after this build:
 * two or more dotted lowercase snake_case segments.
 */
export const PERMISSION_KEY_PATTERN = /^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*){1,3}$/;

/** Accepts only keys in the catalogue. */
export const permissionKeySchema = z.enum(PERMISSION_KEYS as [PermissionKey, ...PermissionKey[]]);

const permissionKeySet: ReadonlySet<string> = new Set(PERMISSION_KEYS);

export function isPermissionKey(value: unknown): value is PermissionKey {
  return typeof value === 'string' && permissionKeySet.has(value);
}

export function permissionDefinition(key: PermissionKey): PermissionDefinition {
  return PERMISSION_CATALOGUE[key];
}

/** Can this key be stored on a tenant role? (`self` and `platform` keys cannot.) */
export function isAssignablePermission(key: PermissionKey): boolean {
  const { scope } = PERMISSION_CATALOGUE[key];
  return scope === 'pos' || scope === 'backoffice';
}

export function isOverridablePermission(key: PermissionKey): boolean {
  return (PERMISSION_CATALOGUE[key] as PermissionDefinition).overridable === true;
}

/** Keys every signed-in staff user holds for their own account. */
export const SELF_PERMISSION_KEYS: readonly PermissionKey[] = PERMISSION_KEYS.filter(
  (k) => PERMISSION_CATALOGUE[k].scope === 'self',
);

/** Keys only QRkey platform staff hold. */
export const PLATFORM_PERMISSION_KEYS: readonly PermissionKey[] = PERMISSION_KEYS.filter(
  (k) => PERMISSION_CATALOGUE[k].scope === 'platform',
);

/** Keys a tenant role may hold. */
export const ASSIGNABLE_PERMISSION_KEYS: readonly PermissionKey[] =
  PERMISSION_KEYS.filter(isAssignablePermission);

/** Keys usable in a till session (the rest need a back-office session). */
export const POS_PERMISSION_KEYS: readonly PermissionKey[] = PERMISSION_KEYS.filter(
  (k) => PERMISSION_CATALOGUE[k].scope === 'pos',
);

/** Keys only background jobs and webhook processing hold. */
export const SYSTEM_PERMISSION_KEYS: readonly PermissionKey[] = PERMISSION_KEYS.filter(
  (k) => PERMISSION_CATALOGUE[k].scope === 'system',
);

/** Prefix the database refuses on `iam.role_permission` (system keys are never on a role). */
export const SYSTEM_PERMISSION_PREFIX = 'system.';

/** Prefix the database refuses on `iam.role_permission` (platform keys are never on a role). */
export const PLATFORM_PERMISSION_PREFIX = 'platform.';

/** Keys only customer actors hold (storefront's diner sessions). */
export const CUSTOMER_PERMISSION_KEYS: readonly PermissionKey[] = PERMISSION_KEYS.filter(
  (k) => PERMISSION_CATALOGUE[k].scope === 'customer',
);

/** Prefix the database refuses on `iam.role_permission` (customer keys are never on a role). */
export const CUSTOMER_PERMISSION_PREFIX = 'store.';

/** An assignable key, as accepted from clients when editing a role. */
export const assignablePermissionKeySchema = permissionKeySchema.refine(isAssignablePermission, {
  message: 'This permission cannot be assigned to a role',
});

/**
 * Mapping from legacy (Laravel) permission names to v3 keys, used by the iam
 * backfill (M2 task 7) to convert existing roles.
 *
 * TODO(legacy): the legacy list of 105 permissions (portal `role-management`,
 * `AdminController`, the JWT middlewares) is not available on this machine.
 * Fill this in from the legacy code; a legacy permission with no v3
 * equivalent maps to an empty array (dropped, recorded in the backfill notes).
 */
export const LEGACY_MAPPING: Readonly<Record<string, readonly PermissionKey[]>> = Object.freeze({});

/** Default role definitions shipped to every company (03-modules.md §2). */
export const DEFAULT_ROLE_KEYS = [
  'owner',
  'manager',
  'cashier',
  'captain',
  'kitchen',
  'bartender',
  'accounts',
] as const;
export type DefaultRoleKey = (typeof DEFAULT_ROLE_KEYS)[number];

export interface DefaultRoleDefinition {
  readonly name: string;
  readonly description: string;
  /** Back-office sign-in needs TOTP for holders of this role. */
  readonly requireTotp: boolean;
  readonly permissions: readonly PermissionKey[];
}

const OWNER_ONLY: ReadonlySet<PermissionKey> = new Set<PermissionKey>([
  'roles.manage',
  'outlet.create',
  'integrations.manage',
  'outlet.feature_switches',
  'customers.privacy',
]);

const CASHIER: readonly PermissionKey[] = [
  'outlet.view',
  'menu.view',
  'menu.availability',
  'orders.view',
  'orders.create',
  'orders.edit',
  'orders.send_kot',
  'orders.void',
  'orders.discount',
  'orders.bill',
  'orders.split',
  'orders.merge',
  'orders.move_table',
  'orders.transfer_lines',
  'orders.accept',
  'orders.fulfilment',
  'orders.tag',
  'orders.sync_offline',
  'kitchen.print_bill',
  'kitchen.test_print',
  'kitchen.print_device',
  'payments.view',
  'payments.record',
  'payments.online',
  'payments.settle',
  'payments.pending_settlement',
  'day.open',
  'invoices.view',
  'shift.open',
  'shift.close',
  'cash.in_out',
  'expenses.record',
  'customers.view',
  'customers.manage',
  'khata.view',
  'khata.charge',
  'khata.settle',
  'rewards.redeem',
  'aggregators.view',
  'aggregators.store_status',
  'delivery.view',
  'delivery.dispatch',
  'staff.offline_pins',
  'notifications.optouts',
];

const CAPTAIN: readonly PermissionKey[] = [
  'outlet.view',
  'menu.view',
  'orders.view',
  'orders.create',
  'orders.edit',
  'orders.send_kot',
  'orders.void',
  'orders.bill',
  'orders.move_table',
  'orders.transfer_lines',
  'orders.fulfilment',
  'orders.tag',
  'orders.sync_offline',
  'kitchen.print_bill',
  'kitchen.print_device',
  'customers.view',
  'staff.offline_pins',
];

export const DEFAULT_ROLES: Readonly<Record<DefaultRoleKey, DefaultRoleDefinition>> = {
  owner: {
    name: 'Owner',
    description: 'Everything in the company, including roles and integrations',
    requireTotp: true,
    permissions: ASSIGNABLE_PERMISSION_KEYS,
  },
  manager: {
    name: 'Manager',
    description: 'Runs the outlet: setup, menu, staff, overrides, reports, day close',
    requireTotp: true,
    permissions: ASSIGNABLE_PERMISSION_KEYS.filter((k) => !OWNER_ONLY.has(k)),
  },
  cashier: {
    name: 'Cashier',
    description: 'Billing at the till: orders, KOTs, bills, tenders, shift cash',
    requireTotp: false,
    permissions: CASHIER,
  },
  captain: {
    name: 'Captain',
    description: 'Takes orders at tables and sends KOTs',
    requireTotp: false,
    permissions: CAPTAIN,
  },
  kitchen: {
    name: 'Kitchen',
    description: 'KDS: tickets, bump and recall, sold-out',
    requireTotp: false,
    permissions: [
      'outlet.view',
      'menu.view',
      'menu.availability',
      'orders.view',
      'orders.fulfilment',
      'kitchen.kds',
      'kitchen.print_device',
      'inventory.items.view',
      'inventory.wastage.record',
    ],
  },
  bartender: {
    name: 'Bartender',
    description: 'Bar orders, permits, breakage and closing counts',
    requireTotp: false,
    permissions: [
      ...CAPTAIN,
      'menu.availability',
      'kitchen.kds',
      'bar.view',
      'bar.permits.assign',
      'bar.breakage',
      'bar.closing',
      'inventory.items.view',
      'inventory.wastage.record',
    ],
  },
  accounts: {
    name: 'Accounts',
    description: 'Reports, cash, expenses, settlements and reconciliation',
    requireTotp: false,
    permissions: [
      'outlet.view',
      'payments.view',
      'payments.pending_settlement',
      'accounting.view',
      'invoices.view',
      'cash.transfer',
      'expenses.record',
      'expenses.manage',
      'expenses.approve',
      'reports.dashboard',
      'reports.view.sales',
      'reports.view.items',
      'reports.view.tax',
      'reports.view.payments',
      'reports.view.voids_discounts',
      'reports.export',
      'inventory.view',
      'bar.view',
      'bar.reports',
      'notifications.view',
      'customers.view',
      'khata.view',
      'khata.settle',
      'aggregators.view',
      'aggregators.settlements',
    ],
  },
};
